'use client';

import { isAxiosError } from 'axios';
import { twMerge } from 'tailwind-merge';
import { useRouter } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { Box, Divider, Typography } from '@wishket/design-system';

import { CollectionEditModal } from '../CollectionModal';
import CollectionContentsList from '../CollectionContentsList';
import CollectionThumbnailModal from '../CollectionThumbnailModal';

import { CollectionDetailListSkeleton } from './CollectionDetail.skeleton';

import {
  useCollectionDetailQuery,
  useCollectionLikeMutation,
  useCollectionScrapMutation,
  useCollectionDeleteMutation,
} from '@/entities/collection';
import {
  CollectionEmpty,
  CollectionListCard,
  CollectionQuickMenu,
} from '@/features/Collection';
import { PATH } from '@/shared/constants';
import { FadeUpContainer } from '@/shared/Components';
import { useDeviceCheck, useProfile, useTimedOpen } from '@/shared/hooks';

interface CollectionDetailProps {
  collectionId: number;
}

const CollectionDetail = ({ collectionId }: CollectionDetailProps) => {
  const router = useRouter();
  const { profile } = useProfile();
  const { isMobile } = useDeviceCheck();
  const cardContainerRef = useRef<HTMLDivElement>(null);
  const [isAffectView, setIsAffectView] = useState(true);

  const { mutate: collectionLike } = useCollectionLikeMutation();
  const { mutate: collectionScrap } = useCollectionScrapMutation();
  const { mutate: collectionDelete, isSuccess: isSuccessDeleteCollection } =
    useCollectionDeleteMutation();
  const [isScrapTooltipOpen, openScrapTooltip, closeScrapTooltip] =
    useTimedOpen();

  const [detailQueryErrorType, setDetailQueryErrorType] = useState<
    'private' | 'empty' | 'deleted' | undefined
  >();

  const {
    error,
    refetch,
    data: collectionDetailData,
  } = useCollectionDetailQuery({
    id: collectionId,
    affectView: isAffectView,
  });

  useEffect(() => {
    setIsAffectView(false);
  }, []);

  const [isThumbCreateModalOpen, setIsThumbCreateModalOpen] = useState(false);
  const [isEditCollectionModalOpen, setIsEditCollectionModalOpen] =
    useState(false);
  const [likeStatus, setLikeStatus] = useState({
    count: 0,
    isActive: false,
  });
  const [scrapStatus, setScrapStatus] = useState({
    count: 0,
    isActive: false,
  });

  const isCollectionOwner =
    Number(profile?.yozmitProfileId) === collectionDetailData?.profile.id;

  useEffect(() => {
    if (collectionDetailData) {
      setLikeStatus({
        isActive: collectionDetailData.isLiked,
        count: collectionDetailData.likeCount,
      });
      setScrapStatus({
        isActive: collectionDetailData.isCollectionScrapped,
        count: collectionDetailData.scrappedCount,
      });
    }
  }, [collectionDetailData]);

  useEffect(() => {
    if (isSuccessDeleteCollection) router.replace(PATH.COLLECTION);
  }, [isSuccessDeleteCollection, router]);

  useEffect(() => {
    if (isAxiosError(error) && error.response) {
      const errorMessage = error.response.data.error.data.detail;
      if (errorMessage === 'Object is private') {
        return setDetailQueryErrorType('private');
      }
      if (errorMessage === 'Object is deleted') {
        return setDetailQueryErrorType('deleted');
      }
      setDetailQueryErrorType('empty');
    }
  }, [error]);

  const handleToggleLike = () => {
    if (profile && collectionDetailData) {
      collectionLike({ id: collectionDetailData.id });
      setLikeStatus({
        isActive: !likeStatus.isActive,
        count: likeStatus.isActive
          ? likeStatus.count - 1
          : likeStatus.count + 1,
      });
    } else {
      router.push(`${PATH.AUTH_LOGIN}?next=${PATH.COLLECTION}/${collectionId}`);
    }
  };

  const handleToggleScrap = () => {
    if (profile && collectionDetailData) {
      collectionScrap(
        { id: collectionDetailData.id },
        {
          onSuccess: ({ status }) => {
            if (status === 201) openScrapTooltip();
          },
        }
      );
      setScrapStatus({
        isActive: !scrapStatus.isActive,
        count: scrapStatus.isActive
          ? scrapStatus.count - 1
          : scrapStatus.count + 1,
      });
    } else {
      router.push(`${PATH.AUTH_LOGIN}?next=${PATH.COLLECTION}/${collectionId}`);
    }
  };

  const openEditThumbnailModal = () => setIsThumbCreateModalOpen(true);

  const closeEditThumbnailModal = () => setIsThumbCreateModalOpen(false);

  const openEditCollectionModal = () => setIsEditCollectionModalOpen(true);

  const closeEditCollectionModal = () => setIsEditCollectionModalOpen(false);

  const handleDeleteCollection = () => {
    if (collectionDetailData) collectionDelete({ id: collectionDetailData.id });
  };

  const handleEditCollectionThumbnailCompleted = () => {
    setIsThumbCreateModalOpen(false);
    refetch();
  };

  const scrollToContentsList = () => {
    if (!isCollectionOwner && isMobile && cardContainerRef.current) {
      const element = cardContainerRef.current.getBoundingClientRect();

      const currentScrollY = window.scrollY;
      const containerHeight = element.height + 32;
      const remainingScroll = containerHeight - currentScrollY;

      window.scrollBy({
        behavior: 'smooth',
        top: remainingScroll,
      });
    }
  };

  return (
    <>
      <Box className="relative grid-in-content">
        {detailQueryErrorType ? (
          <CollectionEmpty type={detailQueryErrorType} />
        ) : !collectionDetailData ? (
          <CollectionDetailListSkeleton />
        ) : !collectionDetailData.isPublic ? (
          <CollectionEmpty type="private" />
        ) : (
          <>
            <FadeUpContainer
              delay={0.2}
              className="flex h-full w-full flex-col gap-8 desktop:gap-12"
            >
              <Box
                ref={cardContainerRef}
                className="flex flex-col gap-8 desktop:gap-12"
              >
                <CollectionListCard
                  {...collectionDetailData}
                  isOwner={isCollectionOwner}
                  likeCount={likeStatus.count}
                  scrappedCount={scrapStatus.count}
                  count={collectionDetailData.scraps.length}
                  onClick={scrollToContentsList}
                  onEditCollection={openEditCollectionModal}
                  onDeleteCollection={handleDeleteCollection}
                  onEditThumbnail={openEditThumbnailModal}
                />
                {collectionDetailData.hashTags.length > 0 && (
                  <Box className="flex flex-wrap gap-3">
                    {collectionDetailData.hashTags.map((tag) => (
                      <Box
                        key={tag.id}
                        className="h-6 rounded-lg bg-w-gray-100 px-2"
                      >
                        <Typography variant="body2" className="text-w-gray-600">
                          {tag.name}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
                <Divider className="desktop:hidden" />
              </Box>
              <CollectionContentsList
                hideAddButton
                isOwner={isCollectionOwner}
                collectionId={collectionDetailData.id}
                articleList={collectionDetailData.scraps}
              />
            </FadeUpContainer>
            <CollectionThumbnailModal
              id={collectionDetailData.id}
              isOpen={isThumbCreateModalOpen}
              image={collectionDetailData.thumbnail}
              onClose={closeEditThumbnailModal}
              onSuccess={handleEditCollectionThumbnailCompleted}
            />
            <CollectionEditModal
              collectionData={collectionDetailData}
              isOpen={isEditCollectionModalOpen}
              onClose={closeEditCollectionModal}
            />
          </>
        )}
      </Box>
      {collectionDetailData && collectionDetailData.isPublic && (
        <Box className="desktop:grid-in-quick-menu">
          <Box
            className={twMerge(
              'fixed bottom-0 left-0 right-0',
              'desktop:sticky desktop:right-0 desktop:top-[209px] desktop:flex desktop:flex-col desktop:items-end'
            )}
          >
            <CollectionQuickMenu
              likeStatus={likeStatus}
              scrapStatus={scrapStatus}
              isOwner={isCollectionOwner}
              shareId={collectionDetailData.id}
              shareTitle={collectionDetailData.title}
              isScrapTooltipOpen={isScrapTooltipOpen}
              toggleLike={handleToggleLike}
              toggleScrap={handleToggleScrap}
              closeScrapTooltip={closeScrapTooltip}
            />
          </Box>
        </Box>
      )}
    </>
  );
};

export default CollectionDetail;
